// Pure presentation component for a task in a list, managing its own data mutations via context hooks.
// @ts-check
import { memo } from 'react';
import { Box, Tooltip } from '@mui/material';

import '@/features/tasks/components/items/TaskItem.css';
import '@/styles/task-shared.css';

import {
  renderGridContent,
  renderListContent,
  getListPositionLabel,
  getRowA11yProps,
} from './TaskItemBase.render';
import { useT } from '@/i18n/useT';
import { useTaskItemBaseController } from './useTaskItemBaseController';
import TaskContextMenu from '@/features/tasks/components/context-menu/TaskContextMenu';
import { useTaskUI } from '@/features/tasks/context/TaskUIContext';
import { getOnboardingHighlightSx } from '@/features/onboarding/utils/onboardingHighlightSx';

/**
 * TaskItemBase — Component that handles its own data mutations via hooks.
 * UI-only props are still passed from parents.
 */
const TaskItemBase = (props) => {
  const t = useT();
  const { itemTabIndex, selectedTaskIds = [], onboardingHighlightTaskId } =
    useTaskUI();
  const {
    task,
    variant,
    isDragDisabled,
    setNodeRef,
    finalStyle,
    classNames,
    eventHandlers,
    displayLevel,
    breadcrumbTooltip,
    contentProps,
    handleDoubleClick,
    menuState,
    closeMenu,
    menuSurface,
    onStartAddingSubtask,
    index,
    tasks,
    isExecutionView,
  } = useTaskItemBaseController(props);

  if (!task || typeof task.title !== 'string') {
    return (
      <li className="task-item invalid-task-item">
        {t('taskitembase.invalidTask', '[Invalid Task]')}
      </li>
    );
  }

  const content =
    variant === 'grid'
      ? renderGridContent(task)
      : renderListContent(contentProps);

  const usesListboxRow = itemTabIndex === -1;
  const isSelected = selectedTaskIds.includes(task.id);
  const listPositionLabel = getListPositionLabel(t, {
    usesListboxRow,
    isExecutionView,
    index,
    task,
    tasks,
  });
  // Breadcrumb lives on the row's aria-label directly — never wrap <li> in MUI
  // Tooltip (cloneElement + data-mui-internal-clone-element breaks Playwright
  // focus/selection locators on the Plan rail).
  const accessibleLabel =
    listPositionLabel ?? (breadcrumbTooltip || undefined);
  const { role, ariaSelected } = getRowA11yProps(usesListboxRow, isSelected);
  const highlightSx = getOnboardingHighlightSx(
    Boolean(onboardingHighlightTaskId && onboardingHighlightTaskId === task.id),
  );

  const rowContent = breadcrumbTooltip ? (
    <Tooltip
      title={breadcrumbTooltip}
      placement="top-start"
      arrow
      enterDelay={400}
    >
      <Box component="span" sx={{ display: 'block', width: '100%' }}>
        {content}
      </Box>
    </Tooltip>
  ) : (
    content
  );

  const listItem = (
    <Box
      component="li"
      ref={isDragDisabled ? undefined : setNodeRef}
      style={finalStyle}
      className={classNames}
      onDoubleClick={handleDoubleClick}
      {...eventHandlers}
      data-task-id={task.id}
      data-testid={`task-item-${task.id}`}
      data-level={displayLevel}
      tabIndex={itemTabIndex}
      role={role}
      aria-selected={ariaSelected}
      aria-label={accessibleLabel}
      sx={highlightSx}
    >
      {rowContent}
    </Box>
  );

  const contextMenu = menuState ? (
    <TaskContextMenu
      task={menuState.task}
      surface={menuSurface}
      anchorPosition={{ mouseX: menuState.mouseX, mouseY: menuState.mouseY }}
      open
      onClose={closeMenu}
      onStartAddingSubtask={onStartAddingSubtask}
    />
  ) : null;

  return (
    <>
      {listItem}
      {contextMenu}
    </>
  );
};

export default memo(TaskItemBase);
